import {Component, inject, Input} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {ErrorPriority} from '../../../enums/ErrorPriority';
import {BaseError} from '../../../models/error/BaseError';
import {BaseErrorService} from '../../../services/error/base-error.service';
import {TranslateService} from '@ngx-translate/core';
import {ToastService} from '../../../services/userInterface/toast.service';
import {LoadingSpinnerService} from '../../../services/userInterface/loading-spinner.service';
import {catchError, of, throwError} from 'rxjs';

@Component({
    selector: 'app-base-error-detail',
    standalone: true,
    imports: [],
    templateUrl: './base-error-detail.component.html',
    styleUrl: './base-error-detail.component.css'
})
export abstract class BaseErrorDetailComponent<T extends BaseError> {
    private route: ActivatedRoute = inject(ActivatedRoute)
    private router: Router = inject(Router);
    private translateService: TranslateService = inject(TranslateService);
    private toastService: ToastService = inject(ToastService);
    private loadingSpinnerService: LoadingSpinnerService = inject(LoadingSpinnerService);

    private errorService: BaseErrorService<T>;
    protected isLoading: boolean = false;
    @Input() error?: T;
    @Input() standalone: boolean = true;

    constructor(errorService: BaseErrorService<T>) {
        this.errorService = errorService;
    }

    public ngOnInit() {
        if (!this.error) {
            this.getSelectedErrorMessage();
        }
    }

    public updateErrorType(errorPriority: ErrorPriority) {
        if (this.error) {
            this.errorService.updateErrorType(this.error, errorPriority).subscribe({
                next: () => {
                    if (this.error) {
                        this.error.errorPriority = errorPriority;
                    }

                    this.toastService.showSuccessMessage(this.translateService.instant("toastMessage.success.update"));
                },
                error: () => {
                    this.toastService.showErrorMessage(this.translateService.instant("toastMessage.error.update"));
                }
            });
        }
    }

    public solveError = () => {
        if (this.error) {
            return this.errorService.solveError(this.error).pipe(
                catchError(error => {
                    this.toastService.showErrorMessage(this.translateService.instant("toastMessage.error.update"));
                    return throwError(() => error);
                })
            )
        }
        return of();
    }

    private async getSelectedErrorMessage() {
        const errorId = this.route.snapshot.paramMap.get("id");
        if (errorId) {
            this.isLoading = true;
            await this.loadingSpinnerService.showSpinner();

            this.errorService.getById(errorId).subscribe({
                next: async (error) => {
                    if (error) {
                        this.error = error;
                    } else {
                        const errorMessage: string = this.translateService.instant("components.errorDetails.notFoundErrorMessage");
                        this.toastService.showErrorMessage(errorMessage);
                        this.navigateToPageNotFound();
                    }

                    await this.loadingSpinnerService.hideSpinner();
                    this.isLoading = false;
                },
                error: async () => {
                    const errorMessage: string = this.translateService.instant("components.errorDetails.loadingErrorMessage");
                    this.toastService.showErrorMessage(errorMessage);
                    this.navigateToDashboard();

                    await this.loadingSpinnerService.hideSpinner();
                    this.isLoading = false;
                }
            });
        }
    }

    private navigateToPageNotFound() {
        this.router.navigate(["/page-not-found"], {replaceUrl: true});
    }

    private async navigateToDashboard() {
        await this.router.navigate(['/dashboard'], {replaceUrl: true});
    }
}
